import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { camelCase } from 'lodash';
import { makeStyles, Typography } from '@material-ui/core';
import { FormattedMessage, useIntl } from 'react-intl';
import { BarChart, ResponsiveContainer, XAxis, YAxis } from 'recharts';
import { HorizontalStackedBar } from '../../../../../../../components/molecules/charts/HorizontalStackedBar';
import { CardStatPanel } from '../../../../../../../components/organims/cards/CardStatPanel';
import { palette } from '../../../../../../../settings/themes/colors';

const CHART_RIGHT_MARGIN = 55;
const SERIES_PALETTE = [palette.azure, palette.captuDataYellow];

const useStyles = makeStyles({
  footer: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: 24,
    flexShrink: 0
  },
  footerGroup: {
    display: 'flex',
    alignItems: 'center',
    gap: 6
  },
  totalLabel: {
    fontSize: 11,
    fontWeight: 700,
    color: palette.dimGray,
    letterSpacing: 0.8,
    textTransform: 'uppercase'
  },
  totalValue: {
    fontSize: 18,
    fontWeight: 900
  },
  axisRuler: {
    flexShrink: 0,
    marginTop: 4,
    borderTop: `1px solid ${palette.platinum}`,
    paddingTop: 2
  }
});

const buildChartData = (data, dimensionKey) =>
  (data || []).map(item => ({
    ...item,
    displayLabel: item[dimensionKey]
  }));

export const StackedBarWidget = ({ widget }) => {
  const classes = useStyles();
  const intl = useIntl();
  const [chartConfig, setChartConfig] = useState(null);

  const { title, help, dimension, series, data } = widget;
  const dimensionKey = (dimension && dimension.key) || 'label';

  const chartSeries = (series || []).map((s, idx) => ({
    key: camelCase(s.key),
    label: s.label,
    stackId: 'a',
    color: s.color || SERIES_PALETTE[idx % SERIES_PALETTE.length]
  }));
  const chartData = buildChartData(data, dimensionKey);

  const renderFooter = () => {
    if (!chartConfig) return null;
    const { seriesTotals, grandTotal, ticks, maxTick, yAxisWidth } = chartConfig;

    return (
      <>
        <div className={classes.footer}>
          <div className={classes.footerGroup}>
            <Typography className={classes.totalLabel}>
              <FormattedMessage id="common.total" />
            </Typography>
            <Typography className={classes.totalValue} style={{ color: palette.dimGray }}>
              {intl.formatNumber(grandTotal)}
            </Typography>
          </div>
          {seriesTotals.map(st => (
            <div key={st.key} className={classes.footerGroup}>
              <Typography className={classes.totalLabel}>
                <FormattedMessage id="common.total" /> {st.label || st.key}
              </Typography>
              <Typography className={classes.totalValue} style={{ color: st.color }}>
                {intl.formatNumber(st.total)}
              </Typography>
            </div>
          ))}
        </div>
        <div className={classes.axisRuler}>
          <ResponsiveContainer width="100%" height={28}>
            <BarChart data={[{ displayLabel: '' }]} layout="vertical" margin={{ top: 0, right: CHART_RIGHT_MARGIN, left: 0, bottom: 4 }}>
              <XAxis
                type="number"
                ticks={ticks}
                domain={[0, maxTick]}
                tick={{ fontSize: 9, fill: palette.dimGray }}
                axisLine={{ stroke: palette.platinum }}
                tickLine={{ stroke: palette.platinum }}
              />
              <YAxis type="category" width={yAxisWidth} tick={false} axisLine={false} tickLine={false} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </>
    );
  };

  return (
    <CardStatPanel title={title} infoTooltip={help} footer={renderFooter()}>
      <HorizontalStackedBar data={chartData} series={chartSeries} onReady={setChartConfig} />
    </CardStatPanel>
  );
};

StackedBarWidget.propTypes = {
  widget: PropTypes.shape({
    title: PropTypes.string,
    help: PropTypes.string,
    dimension: PropTypes.shape({
      key: PropTypes.string,
      label: PropTypes.string
    }),
    series: PropTypes.arrayOf(
      PropTypes.shape({
        key: PropTypes.string.isRequired,
        label: PropTypes.string,
        color: PropTypes.string
      })
    ).isRequired,
    data: PropTypes.array.isRequired
  }).isRequired
};
